/* Koban (Trip-coins) cart module — honest redemption states + exact-hold release
 * saga. No third-party dependencies. Renders identically in the cart PAGE and the
 * cart DRAWER (every [data-tripcoins-cart] module on the page is driven together).
 *
 * Sol redemption REDESIGN (demands 5, 6, 7):
 *  - The module is NEVER removed on ineligible / error / backend-down. A blank is
 *    exactly how Koban became invisibly "gone". Every situation has an honest state.
 *  - The cart quote exposes the RESERVATION itself (hold amount + code + version),
 *    so "A$X reserved" is distinguishable from "gone".
 *  - Release targets an EXACT hold_id + version and runs as a saga: the server does
 *    its half (deactivate the Shopify code + release the reservation); only this
 *    surface can remove the code from its own cart. State is re-read after each
 *    mutation, and the two partial-failure outcomes are surfaced honestly.
 *
 * STATE TABLE (every render path routes to one of these — never a blank):
 *   loading                → reading the quote.
 *   available              → quote.eligible, no reservation. Toggle → redeem.
 *   unavailable+reason     → not eligible, no reservation (no Koban / below-min /
 *                            cart-too-small). Always shows WHY.
 *   below-minimum          → eligible, but THIS cart's subtotal is under the server's
 *                            minimum_subtotal_cents (the non-consented benefit cap the
 *                            minted coupon enforces Shopify-side). Shows "spend at least
 *                            A$X to use them" instead of the toggle — a client-side
 *                            DISPLAY comparison of the SERVER minimum vs the cart.
 *   issuing                → reservation.status === 'issuing'. Refresh re-reads.
 *   reserved-attached      → reservation live AND Shopify's cart_level_discount_
 *                            applications CONFIRM the code is on this cart. Remove.
 *   reserved-unknown       → reservation live but attachment UNCONFIRMED (Shopify
 *                            does not surface manual codes reliably). Apply (an
 *                            idempotent re-apply) + Release. Never a false "off".
 *   applying               → transient after redeem/apply, before the Ajax cart
 *                            discount update completes.
 *   released-clearing      → transient after a confirmed server release, before the
 *                            targeted Ajax discount replacement.
 *   release-unconfirmed    → server released:false (fail-closed) — still reserved,
 *                            Retry release.
 *   expired-stale-code     → the reserved code has expired; Remove the stale code.
 *                            Reachable THREE ways: a live-but-expired reservation; via
 *                            the cleanup marker when the hold is already gone server
 *                            -side but the code is stuck; AND (finding 3 remaining
 *                            branch) with NO reservation and NO marker when Shopify
 *                            surfaces a stale prefix-matching code in cart_level_
 *                            discount_applications (expired-and-swept before any click).
 *   clear-failed           → server released the hold, but the targeted Ajax update
 *                            did NOT remove the code (cleanup marker + code still on
 *                            the cart). Remove code (retry the replacement).
 *   backend-unavailable    → quote/release unreachable. Retry + last-known STALE.
 */
(() => {
  'use strict';

  if (window.KNSTripcoins) return;
  window.KNSTripcoins = true;

  const API_ROOT = '/apps/tripcoins/loyalty';
  const REQUEST_TIMEOUT_MS = 5000;
  const KOBAN_CODE_PREFIX = 'KOBAN-';
  const PERK_CODE_PREFIX = 'KNS-PERK-';
  // A pending cart-cleanup marker (demand 7 / Sol finding 3). When a release or an
  // expiry clears the hold server-side, the discount CODE may still be stuck on this
  // cart, and once the hold is gone the quote's ``reservation`` is null — so without
  // a marker the stale-code / clear-failed states are unreachable. The marker carries
  // the code string across a targeted Ajax removal attempt (and a possible page
  // reload) and is only dropped once a post-update re-check can no longer see it.
  const CLEANUP_STORAGE_KEY = 'kns.tripcoins.cleanup.v1';
  let busy = false;
  let infoSeq = 0;
  let discountsMarkup = '';
  let perkWarningLogged = false;
  // Last quote we successfully rendered — shown (marked STALE) if the backend then
  // goes unreachable, so a transient failure never blanks the module.
  let lastKnownState = null;

  const cartModules = () => Array.from(
    document.querySelectorAll('[data-tripcoins-cart]:not([data-tripcoins-logged-out])')
  );

  function integer(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function cartSubtotal(module) {
    if (!module) return null;
    const value = Number.parseInt(module.dataset.cartSubtotalCents || '', 10);
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function aDollars(dollars) {
    return `A$${new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars)}`;
  }

  function aCents(cents) {
    return aDollars(cents / 100);
  }

  function points(value) {
    return new Intl.NumberFormat('en-AU').format(value);
  }

  // Pure calculation seam for the perk/cart contract. It does not fetch, render, or
  // mutate cart state, so the production integration and the tcdebug check below can
  // reason about the same data independently.
  const KNSPerkMath = (() => {
    const money = (value) => (typeof value === 'number'
      && Number.isSafeInteger(value) && value >= 0 ? value : null);
    const code = (value) => (typeof value === 'string' && value.trim()
      ? value.trim() : null);
    const upper = (value) => (code(value) || '').toUpperCase();

    function applications(cart) {
      return Array.isArray(cart && cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications : [];
    }

    function applicationCodes(cart) {
      const seen = new Set();
      const values = [];
      applications(cart).forEach((application) => {
        if (!application || application.type !== 'discount_code') return;
        // Ajax responses normally expose `code`; Shopify uses `title` as the
        // documented fallback on some cart surfaces. Never submit both: a display
        // title is not necessarily a reusable discount code.
        const candidate = code(application.code) || code(application.title);
        const key = upper(candidate);
        if (candidate && !seen.has(key)) {
          seen.add(key);
          values.push(candidate);
        }
      });
      return values;
    }

    function hasCode(cart, wanted) {
      const target = upper(wanted);
      return Boolean(target) && applicationCodes(cart).some((value) => upper(value) === target);
    }

    function livePerk(response) {
      const perk = response && response.perk;
      if (!response || response.perks_unavailable === true || !perk || perk.state !== 'live') return null;
      if (!code(perk.code) || !upper(perk.code).startsWith(PERK_CODE_PREFIX)
        || money(perk.face_cents) === null || !Array.isArray(perk.breakdown)) return null;
      let face = 0;
      const breakdown = [];
      for (const item of perk.breakdown) {
        if (!item || typeof item.label !== 'string' || money(item.coupon_cents) === null) return null;
        face += item.coupon_cents;
        breakdown.push({ label: item.label, cents: item.coupon_cents });
      }
      return face === perk.face_cents ? { code: perk.code, faceCents: perk.face_cents, breakdown } : null;
    }

    function automaticHandleLines(cart) {
      const fromCartLevel = applications(cart).reduce((lines, application) => {
        if (!application || application.type !== 'automatic') return lines;
        const label = [application.title, application.description]
          .filter((value) => typeof value === 'string').join(' ');
        if (!/handle upgrade/i.test(label)) return lines;
        const cents = money(application.total_allocated_amount);
        lines.push({ kind: 'automatic-handle-upgrade', label: 'Handle Upgrade', cents });
        return lines;
      }, []);
      if (fromCartLevel.length) return fromCartLevel;
      // The live BxGy allocates at LINE level (observed 2026-07-24: /cart.js
      // reports it only in items[].line_level_discount_allocations, never in
      // cart_level_discount_applications) — sum those allocations so the
      // itemized lines visibly add up to Total discounts (Sol demand 22).
      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      // Live shape (observed 2026-07-24): line-level discount_application.type
      // is an EMPTY string even for the automatic BxGy — so the filter accepts
      // any non-code application whose title matches, and additionally excludes
      // any label equal to an attached code (a merchant code literally named
      // "HANDLE UPGRADE…" must never be counted as the automatic).
      const attachedCodes = new Set(applicationCodes(cart).map(upper));
      let cents = 0;
      let seen = false;
      for (const item of items) {
        const allocations = Array.isArray(item && item.line_level_discount_allocations)
          ? item.line_level_discount_allocations : [];
        for (const allocation of allocations) {
          const application = allocation && allocation.discount_application;
          if (!application || application.type === 'discount_code') continue;
          const label = [application.title, application.description]
            .filter((value) => typeof value === 'string').join(' ');
          if (!/handle upgrade/i.test(label)) continue;
          if (attachedCodes.has(upper(application.title || ''))) continue;
          const amount = money(allocation.amount);
          if (amount === null) continue;
          seen = true;
          cents += amount;
        }
      }
      return seen ? [{ kind: 'automatic-handle-upgrade', label: 'Handle Upgrade', cents }] : [];
    }

    function compute(response, cart, options = {}) {
      const perk = livePerk(response);
      const koban = options.koban;
      const perkLines = perk ? perk.breakdown.map((item) => ({
        kind: 'perk', label: item.label, cents: item.cents,
      })) : [];
      const kobanCents = koban && money(koban.cents);
      const kobanLine = koban && code(koban.code) && kobanCents !== null && hasCode(cart, koban.code)
        ? [{ kind: 'koban', label: 'Koban', cents: kobanCents }] : [];
      const ownedCodes = new Set([
        perk && upper(perk.code),
        koban && upper(koban.code),
      ].filter(Boolean));
      const merchantCodePresent = applicationCodes(cart).some((value) => {
        const normalized = upper(value);
        return !ownedCodes.has(normalized)
          && !normalized.startsWith(KOBAN_CODE_PREFIX)
          && !normalized.startsWith(PERK_CODE_PREFIX);
      });
      return {
        visible: Boolean(perk),
        perkCode: perk && perk.code,
        perkFaceCents: perk && perk.faceCents,
        perkApplied: Boolean(perk) && hasCode(cart, perk.code),
        perkLines,
        lines: [...perkLines, ...kobanLine, ...automaticHandleLines(cart)],
        totalDiscountCents: money(cart && cart.total_discount),
        merchantCodePresent,
      };
    }

    return { applicationCodes, hasCode, livePerk, compute };
  })();
  window.KNSPerkMath = KNSPerkMath;

  // --- transport -------------------------------------------------------------

  async function requestRaw(path, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${API_ROOT}${path}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
        ...options,
      });
      let body = null;
      try { body = await response.json(); } catch (_) { body = null; }
      return { ok: response.ok, status: response.status, body };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function requestJSON(path, options = {}) {
    const { ok, body } = await requestRaw(path, options);
    if (!ok || !body) throw new Error('Koban request failed');
    return body;
  }

  async function fetchAjaxCart() {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      let cart = null;
      try { cart = await response.json(); } catch (_) { cart = null; }
      if (!response.ok || !cart || !Array.isArray(cart.items)) throw new Error('Cart read failed');
      return cart;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function perkCartPayload(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid Ajax cart');
    const items = cart.items.map((item) => {
      const variantId = item && item.variant_id;
      const quantity = item && item.quantity;
      if (typeof variantId !== 'number' || !Number.isSafeInteger(variantId) || variantId <= 0
        || typeof quantity !== 'number' || !Number.isSafeInteger(quantity) || quantity <= 0) {
        throw new Error('Invalid Ajax cart line');
      }
      return { variant_id: variantId, quantity };
    });
    return { cart: { items } };
  }

  function validEnsurePerk(value) {
    if (!value || typeof value.perks_unavailable !== 'boolean') return false;
    if (value.perks_unavailable) return true;
    if (!value.perk || (value.perk.state !== 'live' && value.perk.state !== 'none')) return false;
    return value.perk.state === 'none' || KNSPerkMath.livePerk(value) !== null;
  }

  async function ensurePerk(cart) {
    const { ok, body } = await requestRaw('/ensure-perk', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(perkCartPayload(cart)),
    });
    if (!ok || !validEnsurePerk(body)) throw new Error('Invalid perk ensure response');
    return body;
  }

  function warnPerkOnce(message) {
    if (perkWarningLogged) return;
    perkWarningLogged = true;
    // Perks are additive: a failure is intentionally quiet for customers and must
    // never prevent Koban or checkout. One diagnostic is enough for operators.
    console.warn(message);
  }

  function observedDiscountCodes(cart) {
    if (!Array.isArray(cart && cart.cart_level_discount_applications)) return null;
    return KNSPerkMath.applicationCodes(cart);
  }

  function uniqueCodes(codes) {
    const seen = new Set();
    return codes.reduce((result, value) => {
      if (typeof value !== 'string' || !value.trim()) return result;
      const trimmed = value.trim();
      const normalized = trimmed.toUpperCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(trimmed);
      }
      return result;
    }, []);
  }

  function sameCodes(left, right) {
    // Set comparison, not sequence: Shopify may surface applications in a
    // different order than we submitted; an order-only difference must never
    // trigger a redundant /cart/update.js rewrite (GLM S3b review, finding 1).
    if (left.length !== right.length) return false;
    const normalize = (codes) => codes.map((value) => value.toUpperCase()).sort();
    const sortedRight = normalize(right);
    return normalize(left).every((value, index) => value === sortedRight[index]);
  }

  async function updateCartDiscountCodes(cart, codes) {
    const current = observedDiscountCodes(cart);
    if (current === null) throw new Error('Cannot safely preserve cart discount codes');
    const next = uniqueCodes(codes);
    if (sameCodes(current, next)) return { cart, changed: false };
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ discount: next.join(',') }),
      });
      let updated = null;
      try { updated = await response.json(); } catch (_) { updated = null; }
      if (!response.ok || !updated || !Array.isArray(updated.items)) {
        throw new Error('Cart discount update failed');
      }
      return { cart: updated, changed: true };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function activeKobanCode(quote) {
    const reservation = quote && quote.reservation;
    return validReservation(reservation) && reservation.status === 'live' ? reservation.code : null;
  }

  function ownedCodesFor(quote, perkResponse) {
    const perk = KNSPerkMath.livePerk(perkResponse);
    return uniqueCodes([activeKobanCode(quote), perk && perk.code]);
  }

  async function reconcileOwnedCodes(cart, quote, perkResponse) {
    const current = observedDiscountCodes(cart);
    if (current === null) throw new Error('Cannot safely preserve cart discount codes');
    const owned = ownedCodesFor(quote, perkResponse);
    const thirdParty = current.filter((code) => {
      const normalized = code.toUpperCase();
      return !normalized.startsWith(KOBAN_CODE_PREFIX) && !normalized.startsWith(PERK_CODE_PREFIX);
    });
    return updateCartDiscountCodes(cart, [...thirdParty, ...owned]);
  }

  async function addOwnedCode(cart, code) {
    const current = observedDiscountCodes(cart);
    if (current === null) throw new Error('Cannot safely preserve cart discount codes');
    return updateCartDiscountCodes(cart, [...current, code]);
  }

  async function removeOwnedCode(cart, code) {
    const current = observedDiscountCodes(cart);
    if (current === null) throw new Error('Cannot safely preserve cart discount codes');
    const wanted = typeof code === 'string' ? code.toUpperCase() : '';
    return updateCartDiscountCodes(cart, current.filter((value) => value.toUpperCase() !== wanted));
  }

  function validQuote(value) {
    const quote = value || {};
    return integer(quote.points) !== null
      && integer(quote.dollars) !== null
      && typeof quote.eligible === 'boolean'
      && typeof quote.min_met === 'boolean';
  }

  async function fetchQuote(subtotalCents) {
    const params = new URLSearchParams();
    if (subtotalCents !== null) params.set('cart_subtotal_cents', String(subtotalCents));
    const quote = await requestJSON(`/quote?${params.toString()}`);
    if (!validQuote(quote)) throw new Error('Invalid Koban quote');
    return quote;
  }

  function validReservation(res) {
    return res
      && typeof res.code === 'string' && res.code
      && integer(res.hold_id) !== null
      && integer(res.version) !== null
      && integer(res.dollars) !== null
      && typeof res.expires_at === 'string'
      && !Number.isNaN(Date.parse(res.expires_at));
  }

  // Whether the reservation's code is currently ON this browser's cart. The server
  // cannot see the browser cart, so attached-here is a client determination.
  //
  // Sol finding 2: read ONLY the field Shopify's Ajax cart actually documents —
  // ``cart_level_discount_applications`` (GET /cart.js). There is NO ``discount_codes``
  // field, and manually-entered discount codes are NOT guaranteed to surface in the
  // applications list at all. So the honest result is three-valued in spirit but
  // collapses to two we can act on: 'attached' ONLY on a positive code match, and
  // 'unknown' otherwise (a cart read that omits our code is NOT proof it is off —
  // never return a false "not attached"). Ref: shopify.dev/docs/api/ajax/reference/cart.
  async function attachmentState(code) {
    const wanted = code.toUpperCase();
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      const apps = cart && cart.cart_level_discount_applications;
      if (Array.isArray(apps)) {
        const on = apps.some((d) => {
          if (!d || d.type !== 'discount_code') return false;
          const title = typeof d.title === 'string' ? d.title.toUpperCase() : '';
          const dcode = typeof d.code === 'string' ? d.code.toUpperCase() : '';
          return title === wanted || dcode === wanted;
        });
        if (on) return 'attached';
      }
      return 'unknown';
    } catch (_) {
      return 'unknown'; // cannot read the cart → unknown, never a false "off"
    }
  }

  // Scan Shopify's documented ``cart_level_discount_applications`` for the FIRST
  // discount_code entry whose code carries ``prefix``; returns that code string, or
  // null. This is the ONLY reliable evidence of a stale KOBAN- code left on the cart
  // after a hold expired-and-was-swept with no reservation and no cleanup marker (Sol
  // finding 3, remaining branch). ``title`` and ``code`` are tested INDEPENDENTLY (an
  // entry may carry one without the other), each null-safe + uppercased — mirroring
  // ``attachmentState``. Same /cart.js contract — codes Shopify does NOT surface stay
  // undetectable (the platform limit accepted in finding 2).
  async function staleCodeOnCart(prefix) {
    const want = prefix.toUpperCase();
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      const apps = cart && cart.cart_level_discount_applications;
      if (!Array.isArray(apps)) return null;
      for (const d of apps) {
        if (!d || d.type !== 'discount_code') continue;
        const title = typeof d.title === 'string' ? d.title : '';
        const dcode = typeof d.code === 'string' ? d.code : '';
        if (title.toUpperCase().startsWith(want)) return title;
        if (dcode.toUpperCase().startsWith(want)) return dcode;
      }
      return null;
    } catch (_) {
      return null; // cannot read the cart → no evidence → leave current behavior
    }
  }

  // --- pending cart-cleanup marker (localStorage; demand 7 / finding 3) --------

  function writeCleanup(code, dollars, reason) {
    if (typeof code !== 'string' || !code) return;
    try {
      window.localStorage.setItem(CLEANUP_STORAGE_KEY, JSON.stringify({
        code, dollars: integer(dollars) !== null ? dollars : null, reason,
      }));
    } catch (_) {
      // storage is a convenience; a browser that blocks it just can't detect a
      // stuck code post-clear — it still never blanks and never over-spends.
    }
  }

  function readCleanup() {
    try {
      const value = JSON.parse(window.localStorage.getItem(CLEANUP_STORAGE_KEY) || 'null');
      if (!value || typeof value.code !== 'string' || !value.code) return null;
      return {
        code: value.code,
        dollars: integer(value.dollars) !== null ? value.dollars : 0,
        reason: value.reason === 'expired' ? 'expired' : 'released',
      };
    } catch (_) {
      return null;
    }
  }

  function clearCleanup() {
    try { window.localStorage.removeItem(CLEANUP_STORAGE_KEY); } catch (_) { /* noop */ }
  }

  // --- owned cart-code updates -------------------------------------------------

  // Preserve every observable third-party code while changing this module's own
  // code. The Ajax cart update accepts the complete comma-separated set; it is the
  // only safe replacement path here (never Shopify's cart-wide /discount/clear).
  async function clearOwnedCartCode(code, dollars, reason) {
    writeCleanup(code, dollars, reason);
    const cart = await fetchAjaxCart();
    const result = await removeOwnedCode(cart, code);
    if (KNSPerkMath.hasCode(result.cart, code)) return false;
    clearCleanup();
    return true;
  }

  async function requestCartClear(code, dollars, reason) {
    if (busy) return;
    setBusy(true);
    try {
      await completeOwnedCodeRemoval(code, dollars, reason);
    } finally {
      setBusy(false);
    }
  }

  async function completeOwnedCodeRemoval(code, dollars, reason, reservation) {
    renderAll(reservation ? releasedClearingMarkup(reservation) : removingCodeMarkup(reason));
    try {
      const cleared = await clearOwnedCartCode(code, dollars, reason);
      if (cleared) {
        await hydrate(); // cart mutation → re-read both Koban and the perk quote
      } else {
        renderClearFailure(code, dollars, reason);
      }
    } catch (_) {
      renderClearFailure(code, dollars, reason);
    }
  }

  // --- markup: the honest states ---------------------------------------------

  const INFO_TIP = 'Koban (小判) &mdash; the gold coin of Edo-period Japan. 100 Koban = A$1 off your order. Minimum redemption 100 &middot; redemptions are one-off.';
  const FINE = 'Points redemptions are one-off. If you refund or cancel an order that used redeemed Koban, that value is forfeited.';

  function box(inner) {
    return `<div class="ktcart-box">${inner}</div>`;
  }

  function titleRow(title) {
    return `<span class="ktcart-titlerow">
      <span class="ktcart-title">${title}</span><button class="ktcart-info" type="button" aria-label="What is Koban?">?</button>
      <span class="ktcart-tip" role="tooltip">${INFO_TIP}</span>
    </span>`;
  }

  function loadingMarkup() {
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy"><span class="ktcart-title">Koban</span>
      <span class="ktcart-sub" aria-live="polite">Checking your Koban&hellip;</span></span>
    </div>`);
  }

  function availableMarkup(quote, subtotalCents) {
    const wholeBalance = Math.floor(quote.points / 100);
    const capped = wholeBalance > quote.dollars
      && subtotalCents !== null && subtotalCents - quote.dollars * 100 >= 100;
    const capMarkup = capped
      ? `<p class="ktcart-cap">You can apply up to ${aDollars(quote.dollars)} on this order.</p>`
      : '';
    return box(`<label class="ktcart-row">
        <input class="ktcart-check" type="checkbox" data-tripcoins-toggle aria-label="Use my Koban">
        <span class="ktcart-copy">
          ${titleRow('Use my Koban')}
          <span class="ktcart-sub">${points(quote.points)} Koban available &middot; worth ${aDollars(quote.dollars)}</span>
        </span>
      </label>
      ${capMarkup}
      <p class="ktcart-fine">Minimum redemption 100 Koban. ${FINE}</p>`);
  }

  // Eligible, but THIS cart is under the server's minimum_subtotal_cents (Sol
  // prescription D). The minted coupon would carry a Shopify minimumRequirement the
  // cart cannot meet, so instead of offering an unusable toggle we state the honest
  // requirement. Comparison of the SERVER minimum vs the DISPLAY subtotal is a
  // client-side display concern only — the boundary itself is enforced by Shopify.
  function belowMinimumMarkup(quote, minimumCents) {
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban')}
          <span class="ktcart-sub">${points(quote.points)} Koban available &middot; worth ${aDollars(quote.dollars)}. Spend at least ${aCents(minimumCents)} to use them on this order.</span>
        </span>
      </div>
      <p class="ktcart-fine">Minimum redemption 100 Koban. ${FINE}</p>`);
  }

  function unavailableMarkup(quote) {
    // Legitimately unavailable — always WITH a reason (never a blank).
    let reason;
    if (!quote.min_met && quote.points <= 0) {
      reason = 'You have no Koban to redeem yet.';
    } else if (!quote.min_met) {
      reason = `You need at least 100 Koban to redeem &mdash; you have ${points(quote.points)}.`;
    } else {
      reason = 'This cart is too small to apply your Koban right now.';
    }
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy">${titleRow('Koban')}
        <span class="ktcart-sub">${reason}</span></span>
    </div>`);
  }

  function issuingMarkup(res) {
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy">${titleRow('Koban')}
        <span class="ktcart-sub" aria-live="polite">Reserving ${aDollars(res.dollars)} of Koban&hellip;</span></span>
      </div>
      <div class="ktcart-actions"><button class="ktcart-btn" type="button" data-tripcoins-refresh>Refresh</button></div>`);
  }

  // Transient state while we attach a freshly-minted or already-reserved code to the
  // cart (Sol finding 4: redeem is APPLYING the code, not releasing it).
  function applyingMarkup(res) {
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy"><span class="ktcart-title">Applying your Koban</span>
      <span class="ktcart-sub" aria-live="polite">Applying ${aDollars(res.dollars)} of Koban &mdash; attaching the code to your cart&hellip;</span></span>
    </div>`);
  }

  function reservedAttachedMarkup(res, subtotalCents, note) {
    const totalCents = subtotalCents !== null
      ? Math.max(0, subtotalCents - res.dollars * 100) : null;
    // While a live perk is being shown, the itemized block below is the source of
    // truth for both the Koban line and Shopify's actual total. Retain the legacy
    // Koban-only summary if perks are unavailable, so a perk outage never changes
    // the Koban experience.
    const summary = !discountsMarkup && totalCents !== null ? `<div class="ktcart-summary">
        <div class="ktcart-line"><span>Subtotal</span><span class="ktcart-amt">${aCents(subtotalCents)}</span></div>
        <div class="ktcart-line ktcart-line-gold">
          <span>Koban<button class="ktcart-remove" data-tripcoins-release type="button">Remove</button></span>
          <span class="ktcart-amt">&minus;${aDollars(res.dollars)}</span>
        </div>
        <div class="ktcart-line ktcart-line-total"><span>Total</span><span class="ktcart-amt">${aCents(totalCents)}</span></div>
      </div>` : `<div class="ktcart-actions"><button class="ktcart-btn" type="button" data-tripcoins-release>Remove Koban</button></div>`;
    return box(`<label class="ktcart-row">
        <input class="ktcart-check" type="checkbox" data-tripcoins-toggle checked aria-label="Koban applied">
        <span class="ktcart-copy">
          <span class="ktcart-title">Koban applied</span>
          <span class="ktcart-sub">Switch off any time before checkout &mdash; your Koban return instantly.</span>
        </span>
      </label>
      ${note ? `<p class="ktcart-note" aria-live="polite">${note}</p>` : ''}
      ${summary}
      <p class="ktcart-fine">${FINE}</p>`);
  }

  // Reservation live, attachment UNCONFIRMED (Sol finding 2). We cannot prove the
  // code is on this cart, so we NEVER claim it is off — we offer an idempotent Apply
  // (re-applying an already-applied code is harmless) plus Release.
  function reservedUnknownMarkup(res, note) {
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban reserved')}
          <span class="ktcart-sub">${aDollars(res.dollars)} of your Koban is reserved. We can't confirm it's applied to this cart &mdash; tap Apply to be sure (re-applying an already-applied code is harmless), or Release to free it.</span>
        </span>
      </div>
      ${note ? `<p class="ktcart-note" aria-live="polite">${note}</p>` : ''}
      <div class="ktcart-actions">
        <button class="ktcart-btn ktcart-btn-gold" type="button" data-tripcoins-apply>Apply ${aDollars(res.dollars)}</button>
        <button class="ktcart-btn" type="button" data-tripcoins-release>Release</button>
      </div>
      <p class="ktcart-fine">${FINE}</p>`);
  }

  function releaseUnconfirmedMarkup(res, attachment) {
    const lead = attachment === 'attached'
      ? `Your Koban is still reserved &mdash; ${aDollars(res.dollars)} is held, and the code may still be on this cart.`
      : `${aDollars(res.dollars)} of your Koban is still reserved.`;
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban still reserved')}
          <span class="ktcart-sub">${lead}</span>
        </span>
      </div>
      <div class="ktcart-actions">
        <button class="ktcart-btn ktcart-btn-gold" type="button" data-tripcoins-release>Retry release</button>
      </div>`);
  }

  function releasedClearingMarkup(res) {
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy"><span class="ktcart-title">Koban released</span>
      <span class="ktcart-sub" aria-live="polite">Released ${aDollars(res.dollars)} &mdash; removing only that code from your cart&hellip;</span></span>
    </div>`);
  }

  function removingCodeMarkup(reason) {
    const label = reason === 'expired' ? 'Removing expired Koban code' : 'Removing Koban code';
    return box(`<div class="ktcart-row">
      <span class="ktcart-copy"><span class="ktcart-title">${label}</span>
      <span class="ktcart-sub" aria-live="polite">Updating your cart discounts&hellip;</span></span>
    </div>`);
  }

  // The hold was released server-side, but the targeted cart discount update did NOT
  // remove the code (marker present AND the code is still confirmed on the cart).
  function clearFailedMarkup(cleanup) {
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban')}
          <span class="ktcart-sub">Your Koban were released, but the discount code is still on this cart. Remove it so your total is correct at checkout.</span>
        </span>
      </div>
      <div class="ktcart-actions">
        <button class="ktcart-btn" type="button" data-tripcoins-clearstale>Remove code</button>
      </div>`);
  }

  function expiredStaleMarkup() {
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban')}
          <span class="ktcart-sub">Your reserved Koban code has expired. Remove it and your Koban are free to use again.</span>
        </span>
      </div>
      <div class="ktcart-actions">
        <button class="ktcart-btn" type="button" data-tripcoins-clearstale>Remove expired code</button>
      </div>`);
  }

  function backendUnavailableMarkup(stale) {
    const staleLine = stale && integer(stale.dollars) !== null
      ? `<span class="ktcart-sub ktcart-stale">Last seen: ${aDollars(stale.dollars)} of Koban (may be out of date).</span>`
      : '<span class="ktcart-sub">We couldn\'t reach your Koban balance.</span>';
    return box(`<div class="ktcart-row">
        <span class="ktcart-copy">${titleRow('Koban')}
          ${staleLine}
        </span>
      </div>
      <div class="ktcart-actions">
        <button class="ktcart-btn" type="button" data-tripcoins-retry>Retry</button>
      </div>`);
  }

  function escapeMarkup(value) {
    return String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
    }[character]));
  }

  function itemizedDiscountsMarkup(calculation, phase) {
    if (!calculation || !calculation.visible) return '';
    const showPerkLines = calculation.perkApplied || phase === 'applying';
    const lines = showPerkLines
      ? calculation.lines : calculation.lines.filter((line) => line.kind !== 'perk');
    const lineMarkup = lines.map((line) => {
      const applying = line.kind === 'perk' && phase === 'applying';
      const amount = integer(line.cents) !== null ? `&minus;${aCents(line.cents)}` : 'Applied';
      return `<div class="ktcart-discount-line${applying ? ' ktcart-discount-applying' : ''}">
        <span>${escapeMarkup(line.label)}${applying ? '<small>Applying&hellip;</small>' : ''}</span>
        <span class="ktcart-amt">${amount}</span>
      </div>`;
    }).join('');
    const settled = !calculation.perkApplied && phase === 'settled'
      ? '<p class="ktcart-discount-note" aria-live="polite">Your member perk code is not reflected in this cart yet.</p>' : '';
    const total = integer(calculation.totalDiscountCents) !== null
      ? `&minus;${aCents(calculation.totalDiscountCents)}` : '—';
    return `<section class="ktcart-discounts" aria-label="Your discounts">
      <h3 class="ktcart-discounts-title">Your discounts</h3>
      <div class="ktcart-discounts-lines">${lineMarkup}</div>
      ${settled}
      <div class="ktcart-discount-line ktcart-discount-total"><span>Total discounts</span><span class="ktcart-amt">${total}</span></div>
      <p class="ktcart-discount-copy">Discounts already included. Checkout groups your member benefits under one short-lived K&amp;S code.</p>
      ${calculation.merchantCodePresent ? '<p class="ktcart-discount-note">Shopify applies the better combination.</p>' : ''}
    </section>`;
  }

  function renderClearFailure(code, dollars, reason) {
    const markup = reason === 'expired' ? expiredStaleMarkup() : clearFailedMarkup({ code, dollars });
    renderAll(markup, wireButtons({
      '[data-tripcoins-clearstale]': () => requestCartClear(code, dollars, reason),
    }));
  }

  // --- render + wiring --------------------------------------------------------

  function setBusy(value) {
    busy = value;
    document.querySelectorAll(
      '[data-tripcoins-toggle],[data-tripcoins-release],[data-tripcoins-apply],[data-tripcoins-clearstale]'
    ).forEach((control) => { control.disabled = value; });
  }

  function renderAll(markup, wire) {
    cartModules().forEach((module) => {
      module.innerHTML = `${markup}${discountsMarkup}`;
      module.hidden = false;
      const info = module.querySelector('.ktcart-info');
      const tip = module.querySelector('.ktcart-tip');
      if (info && tip) {
        const tipId = `ktcart-tip-${(infoSeq += 1)}`;
        tip.id = tipId;
        info.setAttribute('aria-describedby', tipId);
      }
      if (busy) {
        module.querySelectorAll(
          '[data-tripcoins-toggle],[data-tripcoins-release],[data-tripcoins-apply],[data-tripcoins-clearstale]'
        ).forEach((control) => { control.disabled = true; });
      }
      if (typeof wire === 'function') wire(module);
    });
  }

  function wireAvailable(subtotalCents) {
    return (module) => {
      const toggle = module.querySelector('[data-tripcoins-toggle]');
      if (toggle) {
        toggle.addEventListener('change', () => {
          if (busy) return;
          if (toggle.checked) redeem(subtotalCents);
        });
      }
    };
  }

  function wireReserved(res, subtotalCents, attachment) {
    return (module) => {
      const toggle = module.querySelector('[data-tripcoins-toggle]');
      if (toggle) {
        toggle.addEventListener('change', () => {
          if (busy) return;
          if (!toggle.checked) release(res, subtotalCents, attachment);
        });
      }
      const rel = module.querySelector('[data-tripcoins-release]');
      if (rel) rel.addEventListener('click', () => release(res, subtotalCents, attachment));
      const apply = module.querySelector('[data-tripcoins-apply]');
      if (apply) apply.addEventListener('click', () => applyExisting(res));
    };
  }

  function wireButtons(handlers) {
    return (module) => {
      Object.entries(handlers).forEach(([sel, fn]) => {
        const el = module.querySelector(sel);
        if (el) el.addEventListener('click', fn);
      });
    };
  }

  // --- actions ----------------------------------------------------------------

  async function redeem(subtotalCents) {
    if (busy) return;
    if (subtotalCents === null) { await hydrate(); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({ cart_subtotal_cents: String(subtotalCents) });
      const dto = await requestJSON(`/redeem?${params.toString()}`, { method: 'POST' });
      if (!validReservation(dto)) throw new Error('Invalid Koban redemption');
      // We are APPLYING the newly-reserved code to the cart (Sol finding 4) — show
      // the applying/attaching progression, not a "released" banner. Keep every
      // observable non-owned code when adding it through Shopify's multi-code field.
      renderAll(applyingMarkup(dto));
      const cart = await fetchAjaxCart();
      await addOwnedCode(cart, dto.code);
      await hydrate(); // cart mutation → ensure the perk against the new cart
    } catch (_) {
      // NEVER blank: re-derive the honest state from a fresh read.
      await hydrate();
    } finally {
      setBusy(false);
    }
  }

  // Apply an ALREADY-RESERVED code to this cart — NEVER a second mint. Just applies
  // the existing reservation's code through Shopify's discount mechanism (idempotent
  // if it is already on the cart).
  async function applyExisting(res) {
    if (busy) return;
    if (!validReservation(res)) { hydrate(); return; }
    setBusy(true);
    try {
      renderAll(applyingMarkup(res));
      const cart = await fetchAjaxCart();
      await addOwnedCode(cart, res.code);
      await hydrate(); // cart mutation → ensure the perk against the new cart
    } catch (_) {
      await hydrate();
    } finally {
      setBusy(false);
    }
  }

  async function release(res, subtotalCents, attachment) {
    if (busy) return;
    if (!validReservation(res)) { await hydrate(); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({
        hold_id: String(res.hold_id), version: String(res.version),
      });
      const { status, body } = await requestRaw(`/release?${params.toString()}`, { method: 'POST' });

      if (status === 409) {
        // Stale/terminal target — the server handed back current truth. If the hold
        // is already gone, our cart code may be stale: mark cleanup and clear (the
        // post-clear re-check confirms). Otherwise re-read fresh state.
        const st = body && body.redemption && body.redemption.status;
        if (st && st !== 'live' && st !== 'issuing') {
          await completeOwnedCodeRemoval(res.code, res.dollars, 'released', res);
          return;
        }
        await hydrate();
        return;
      }
      if (body && body.released === true) {
        // Server did its half. Remove just this owned code through the Ajax cart
        // multi-code field; third-party codes remain in the submitted set.
        await completeOwnedCodeRemoval(res.code, res.dollars, 'released', res);
        return;
      }
      // released:false → the code could not be proven dead: STILL reserved (fail
      // -closed). Surface it honestly with a Retry (never a silent blank).
      const current = (body && body.redemption && validReservation(body.redemption))
        ? body.redemption : res;
      lastKnownState = { dollars: current.dollars };
      renderAll(releaseUnconfirmedMarkup(current, attachment), wireReserved(current, subtotalCents, attachment));
    } catch (_) {
      renderAll(backendUnavailableMarkup(lastKnownState), wireButtons({
        '[data-tripcoins-retry]': () => release(res, subtotalCents, attachment),
      }));
    } finally {
      setBusy(false);
    }
  }

  // No-reservation, no-marker branch of Sol finding 3: a hold that expired AND was
  // swept before any client interaction never wrote a cleanup marker, so the normal
  // path would fall straight to availability even while Shopify is surfacing the stale
  // code. Scan for a discount_code entry carrying our redemption prefix (config-driven
  // via the quote DTO, with a hardcoded fallback only if the field is absent); on a
  // match, render the expired-stale-code state and wire its Remove action to
  // a targeted cart discount update — which writes the marker, handing off to the
  // finding-3 retry loop without clearing third-party codes.
  // Returns true iff a stale code was found and rendered.
  async function adoptStaleCode(quote) {
    const prefix = (typeof quote.redeem_code_prefix === 'string' && quote.redeem_code_prefix)
      ? quote.redeem_code_prefix : 'KOBAN-';
    const staleCode = await staleCodeOnCart(prefix);
    if (!staleCode) return false;
    renderAll(expiredStaleMarkup(), wireButtons({
      '[data-tripcoins-clearstale]': () => requestCartClear(staleCode, 0, 'expired'),
    }));
    return true;
  }

  // --- hydrate: derive the honest state from server truth ---------------------

  async function renderState(quote, subtotalCents) {
    const res = quote.reservation;
    if (res && validReservation(res)) {
      lastKnownState = { dollars: res.dollars };
      if (res.status === 'issuing') {
        renderAll(issuingMarkup(res), wireButtons({
          '[data-tripcoins-refresh]': () => hydrate(),
        }));
        return;
      }
      if (Date.parse(res.expires_at) <= Date.now()) {
        renderAll(expiredStaleMarkup(), wireButtons({
          '[data-tripcoins-clearstale]': () => requestCartClear(res.code, res.dollars, 'expired'),
        }));
        return;
      }
      const attachment = await attachmentState(res.code);
      if (attachment === 'attached') {
        renderAll(reservedAttachedMarkup(res, subtotalCents), wireReserved(res, subtotalCents, 'attached'));
      } else {
        renderAll(reservedUnknownMarkup(res), wireReserved(res, subtotalCents, 'unknown'));
      }
      return;
    }
    // No live reservation. Before the normal eligible/unavailable decision, honour any
    // pending cart-cleanup marker (Sol finding 3): a release or expiry cleared the
    // hold server-side, but the discount CODE may still be stuck on this cart. The
    // marker survived a previous targeted removal attempt so we can CONFIRM here.
    const cleanup = readCleanup();
    if (cleanup) {
      const attachment = await attachmentState(cleanup.code);
      if (attachment === 'attached') {
        // Confirmed still on the cart → the clear did NOT take. Honest terminal state,
        // with a safe (idempotent) retry. Marker stays until the code is gone.
        if (cleanup.reason === 'expired') {
          renderAll(expiredStaleMarkup(), wireButtons({
            '[data-tripcoins-clearstale]': () => requestCartClear(cleanup.code, cleanup.dollars, 'expired'),
          }));
        } else {
          renderAll(clearFailedMarkup(cleanup), wireButtons({
            '[data-tripcoins-clearstale]': () => requestCartClear(cleanup.code, cleanup.dollars, 'released'),
          }));
        }
        return;
      }
      // Not confirmed on the cart → best evidence is the clear took (Shopify does not
      // surface manual codes reliably, so we cannot loop forever waiting for proof).
      // Drop the marker and fall through to the fresh honest state.
      clearCleanup();
    } else if (await adoptStaleCode(quote)) {
      // No reservation AND no marker, but Shopify IS surfacing a stale KOBAN- code on
      // this cart — a hold that expired-and-was-swept before any interaction. Use that
      // evidence (Sol finding 3, remaining branch): the expired-stale-code state is
      // rendered and its Remove action hands off to the finding-3 cleanup loop.
      return;
    }
    if (quote.eligible) {
      lastKnownState = { dollars: quote.dollars };
      // If THIS cart is under the server's minimum (the non-consented benefit cap the
      // coupon enforces Shopify-side), the code would not apply — show the honest
      // "spend at least A$X" requirement rather than an unusable toggle (Sol
      // prescription D). Display comparison only; the boundary is Shopify's.
      const minimumCents = integer(quote.minimum_subtotal_cents) !== null
        ? quote.minimum_subtotal_cents : 0;
      if (minimumCents > 0 && subtotalCents !== null && subtotalCents < minimumCents) {
        renderAll(belowMinimumMarkup(quote, minimumCents));
        return;
      }
      renderAll(availableMarkup(quote, subtotalCents), wireAvailable(subtotalCents));
      return;
    }
    // Not eligible, nothing reserved → legitimately unavailable, WITH a reason.
    renderAll(unavailableMarkup(quote));
  }

  async function hydrate() {
    const modules = cartModules();
    if (!modules.length) return;
    const subtotalCents = cartSubtotal(modules[0]);
    discountsMarkup = '';
    renderAll(loadingMarkup()); // never blank while we read
    let cart = null;
    try {
      cart = await fetchAjaxCart();
    } catch (_) {
      // The cart read is required for the price-free ensure payload. Koban retains
      // its existing independent quote/error state; perks simply stay invisible.
      warnPerkOnce('Tripcoins perk cart read failed; hiding perk display.');
    }

    let quote;
    try {
      quote = await fetchQuote(subtotalCents);
    } catch (_) {
      // Backend unreachable / unauthorized / rate-limited: show the last known
      // state marked STALE with a Retry — the module stays, never blanks.
      renderAll(backendUnavailableMarkup(lastKnownState), wireButtons({
        '[data-tripcoins-retry]': () => hydrate(),
      }));
      return;
    }

    if (!cart) {
      await renderState(quote, subtotalCents);
      return;
    }

    let perkResponse;
    try {
      perkResponse = await ensurePerk(cart);
    } catch (_) {
      warnPerkOnce('Tripcoins perk ensure failed; hiding perk display.');
      await renderState(quote, subtotalCents);
      return;
    }

    if (perkResponse.perks_unavailable) {
      await renderState(quote, subtotalCents);
      return;
    }

    // Reconcile only the two prefixes we own. The same operation preserves every
    // observed merchant code and removes a stale/replaced KNS-PERK code without
    // calling Shopify's cart-wide clear endpoint.
    try {
      const firstOwned = ownedCodesFor(quote, perkResponse);
      let result = await reconcileOwnedCodes(cart, quote, perkResponse);
      cart = result.cart;
      if (result.changed) {
        // A cart discount mutation is a cart mutation: immediately ensure again
        // with its Ajax cart lines before trusting the itemized display.
        perkResponse = await ensurePerk(cart);
        if (perkResponse.perks_unavailable) {
          discountsMarkup = '';
          await renderState(quote, subtotalCents);
          return;
        }
        // A code can be replaced between the first response and the post-update
        // ensure. Reconcile that replacement once; do not re-post an unchanged code
        // merely because Shopify has not surfaced its application yet.
        if (!sameCodes(firstOwned, ownedCodesFor(quote, perkResponse))) {
          result = await reconcileOwnedCodes(cart, quote, perkResponse);
          cart = result.cart;
          if (result.changed) {
            perkResponse = await ensurePerk(cart);
            if (perkResponse.perks_unavailable) {
              discountsMarkup = '';
              await renderState(quote, subtotalCents);
              return;
            }
          }
        }
      }
    } catch (_) {
      warnPerkOnce('Tripcoins perk discount update failed; hiding perk display.');
      await renderState(quote, subtotalCents);
      return;
    }

    const livePerk = KNSPerkMath.livePerk(perkResponse);
    if (!livePerk) {
      await renderState(quote, subtotalCents);
      return;
    }

    const reservation = quote.reservation;
    const koban = validReservation(reservation) && reservation.status === 'live'
      ? { code: reservation.code, cents: reservation.dollars * 100 } : null;
    let calculation = KNSPerkMath.compute(perkResponse, cart, { koban });
    if (!calculation.perkApplied) {
      // Shopify can accept a code before /cart.js reports the application. Render
      // the exact backend breakdown as applying, poll once, then redraw from the
      // cart result rather than continuing to claim an intended discount.
      discountsMarkup = itemizedDiscountsMarkup(calculation, 'applying');
      await renderState(quote, subtotalCents);
      try {
        cart = await fetchAjaxCart();
        calculation = KNSPerkMath.compute(perkResponse, cart, { koban });
      } catch (_) {
        calculation = KNSPerkMath.compute(perkResponse, null, { koban });
      }
    }
    discountsMarkup = itemizedDiscountsMarkup(
      calculation, calculation.perkApplied ? 'applied' : 'settled'
    );
    await renderState(quote, subtotalCents);
  }

  function runPerkMathDebugCheck() {
    const assert = (condition, message) => {
      if (!condition) throw new Error(`KNSPerkMath tcdebug failed: ${message}`);
    };
    const response = {
      perks_unavailable: false,
      perk: {
        state: 'live', code: 'KNS-PERK-DEBUG', face_cents: 4180,
        breakdown: [
          { label: 'Shirogami handle perk (50%)', coupon_cents: 1980 },
          { label: 'Free rehandling', coupon_cents: 2200 },
        ],
      },
    };
    const cart = {
      items: [], total_discount: 5980,
      cart_level_discount_applications: [
        { type: 'discount_code', code: 'KNS-PERK-DEBUG' },
        { type: 'discount_code', code: 'KOBAN-DEBUG' },
        { type: 'automatic', title: 'Handle Upgrade', total_allocated_amount: 800 },
        { type: 'discount_code', code: 'WELCOME10' },
      ],
    };
    const calculation = KNSPerkMath.compute(response, cart, {
      koban: { code: 'KOBAN-DEBUG', cents: 1000 },
    });
    assert(calculation.visible && calculation.perkApplied, 'live perk must be applied');
    assert(calculation.perkLines.reduce((sum, line) => sum + line.cents, 0) === 4180,
      'perk lines must equal the coupon face');
    assert(calculation.lines.length === 4, 'perk, Koban, and automatic lines must be itemized');
    assert(calculation.totalDiscountCents === 5980, 'total must come from Ajax cart actuals');
    assert(calculation.merchantCodePresent, 'merchant code must trigger the combination note');
    // The live BxGy reports ONLY line-level allocations (no cart-level automatic
    // application) — the Handle Upgrade row must still be itemized from them.
    const lineLevel = KNSPerkMath.compute(response, {
      items: [{
        line_level_discount_allocations: [
          // Live shape: type is an EMPTY string on the automatic BxGy.
          { amount: 500, discount_application: { type: '', title: 'Handle Upgrade Discount' } },
          { amount: 300, discount_application: { type: '', title: 'Handle Upgrade Discount' } },
          { amount: 999, discount_application: { type: '', title: 'Some Other Sale' } },
          { amount: 777, discount_application: { type: 'discount_code', title: 'HANDLE UPGRADE 10' } },
        ],
      }],
      total_discount: 5980,
      cart_level_discount_applications: cart.cart_level_discount_applications
        .filter((application) => application.type !== 'automatic'),
    }, { koban: { code: 'KOBAN-DEBUG', cents: 1000 } });
    const lineLevelAutomatic = lineLevel.lines.filter(
      (line) => line.kind === 'automatic-handle-upgrade',
    );
    assert(lineLevelAutomatic.length === 1 && lineLevelAutomatic[0].cents === 800,
      'line-level Handle Upgrade allocations must sum into one itemized row');
    const pending = KNSPerkMath.compute(response, {
      ...cart,
      cart_level_discount_applications: cart.cart_level_discount_applications
        .filter((application) => application.code !== 'KNS-PERK-DEBUG'),
    }, { koban: { code: 'KOBAN-DEBUG', cents: 1000 } });
    assert(!pending.perkApplied && pending.perkLines.length === 2,
      'unreported code must retain the exact applying breakdown for one poll');
  }

  if (new URLSearchParams(window.location.search).get('tcdebug') === '1') {
    runPerkMathDebugCheck();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate, { once: true });
  } else {
    hydrate();
  }
  document.addEventListener('shopify:section:load', hydrate);
})();
